import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import {
  ConsultRoomAudioRecorder,
  type ConsultRoomRecordingPhase,
} from './consultRoomAudioRecorder';
import { EVOLUTION_THEME } from './evolutionTheme';

export type EvolutionConsultAudioApplyPayload = {
  evolution_free_text: string;
  diagnosis?: string | null;
  diagnosis_code?: string | null;
  internal_notes?: string | null;
  follow_up_notes?: string | null;
  transcript?: string;
};

type Props = {
  disabled?: boolean;
  patientName?: string;
  professionalName?: string;
  medicalHistory?: string;
  onApply: (payload: EvolutionConsultAudioApplyPayload) => void;
  onError?: (message: string) => void;
  /** Cabeçalho já visível no pai (modo notebook). */
  compact?: boolean;
};

const AUDIO_CAPTURE_HELP =
  'Grave a conversa com o paciente; ao finalizar, a IA transcreve e preenche o texto livre da evolução (não é cópia literal da transcrição). Use apenas com consentimento do paciente.';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const EvolutionConsultAudioPanel: React.FC<Props> = ({
  disabled = false,
  patientName = '',
  professionalName = '',
  medicalHistory = '',
  onApply,
  onError,
  compact = false,
}) => {
  const recorderRef = useRef<ConsultRoomAudioRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [phase, setPhase] = useState<ConsultRoomRecordingPhase>('idle');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [hint, setHint] = useState('');
  const [lastTranscript, setLastTranscript] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const getRecorder = useCallback(() => {
    if (!recorderRef.current) recorderRef.current = new ConsultRoomAudioRecorder();
    return recorderRef.current;
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      recorderRef.current?.dispose();
    },
    [],
  );

  const startRecording = async () => {
    if (disabled || phase === 'processing') return;
    try {
      setLastTranscript(null);
      setShowTranscript(false);
      setHint('Captando áudio do consultório. Peça consentimento do paciente antes de gravar.');
      await getRecorder().start();
      setPhase('recording');
      setElapsedSec(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setElapsedSec((n) => n + 1), 1000);
    } catch (e: any) {
      setPhase('failed');
      const msg = e?.message || 'Não foi possível acessar o microfone.';
      setHint(msg);
      onError?.(msg);
    }
  };

  const stopAndGenerate = async () => {
    if (phase !== 'recording') return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      setPhase('processing');
      setHint('Enviando áudio para transcrição e elaboração da evolução…');
      const blob = await getRecorder().stop();
      const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      const fd = new FormData();
      fd.append('audio', blob, `consultorio-${Date.now()}.${ext}`);
      if (patientName.trim()) fd.append('patient_name', patientName.trim());
      if (professionalName.trim()) fd.append('professional_name', professionalName.trim());
      if (medicalHistory.trim()) fd.append('medical_history', medicalHistory.trim());

      const res = await axios.post(getApiUrl('/clinic/evolutions/ai-from-consult-audio'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      });

      const data = res.data?.data;
      if (!data?.evolution_free_text) {
        throw new Error('Resposta inválida do servidor.');
      }

      setLastTranscript(typeof data.transcript === 'string' ? data.transcript : null);
      onApply({
        evolution_free_text: data.evolution_free_text,
        diagnosis: data.diagnosis,
        diagnosis_code: data.diagnosis_code,
        internal_notes: data.internal_notes,
        follow_up_notes: data.follow_up_notes,
        transcript: data.transcript,
      });
      setPhase('done');
      setHint('Evolução gerada a partir do áudio. Revise o texto antes de concluir.');
    } catch (e: any) {
      setPhase('failed');
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Falha ao processar o áudio. Tente novamente.';
      setHint(msg);
      onError?.(msg);
    }
  };

  const recording = phase === 'recording';
  const busy = phase === 'processing';

  return (
    <Box
      sx={{
        mb: compact ? 0.75 : 1.5,
        px: compact ? 1 : 1.5,
        py: compact ? 0.65 : 1,
        borderRadius: 1.5,
        border: `1px solid ${EVOLUTION_THEME.border}`,
        bgcolor: 'rgba(15, 23, 42, 0.02)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent={compact ? 'flex-end' : 'space-between'} gap={1} flexWrap="wrap">
        {!compact && (
          <Tooltip title={AUDIO_CAPTURE_HELP} placement="top" enterDelay={300}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: '0.8rem',
                color: EVOLUTION_THEME.navy,
                cursor: 'help',
                lineHeight: 1.3,
              }}
            >
              Captura de áudio do consultório
            </Typography>
          </Tooltip>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          {recording && (
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
              {formatElapsed(elapsedSec)}
            </Typography>
          )}
          {!recording ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<MicIcon />}
              disabled={disabled || busy}
              onClick={() => void startRecording()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Iniciar gravação
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => void stopAndGenerate()}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Finalizar e gerar evolução
            </Button>
          )}
        </Stack>
      </Stack>

      {busy && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress />
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: EVOLUTION_THEME.vitalsAccent }} />
            <Typography variant="caption" sx={{ color: '#475569' }}>
              Transcrevendo e estruturando a evolução…
            </Typography>
          </Stack>
        </Box>
      )}

      {hint && !busy && (
        <Alert
          severity={phase === 'failed' ? 'error' : phase === 'done' ? 'success' : 'info'}
          sx={{ mt: compact ? 0.75 : 1.5, py: compact ? 0 : undefined, borderRadius: compact ? 1.5 : 2 }}
        >
          {hint}
        </Alert>
      )}

      {lastTranscript && phase === 'done' && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setShowTranscript((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
          >
            {showTranscript ? 'Ocultar transcrição bruta' : 'Ver transcrição bruta (referência)'}
          </Button>
          <Collapse in={showTranscript}>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: '#fff',
                border: '1px solid rgba(148,163,184,0.35)',
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {lastTranscript}
            </Typography>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default EvolutionConsultAudioPanel;
